import React from 'react';
import { REQUEST_ACCESS_URL, SIGN_IN_URL } from '../config/links';

const serif = "Georgia, 'Times New Roman', serif";
const sans = "ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
const mono = "ui-monospace, 'SF Mono', 'Cascadia Code', Menlo, Consolas, monospace";

/* Shared footer with site navigation + legal/trust links, used by every
   public page. Designed to read on the notebook (cream paper) palette.
   The link columns double as the site's internal-link graph for SEO —
   the audience pages, essays, and /start have no other persistent home. */

const COLUMNS = [
  {
    title: 'Product',
    links: [
      { href: 'https://demo.factory-os.com', label: 'Take the 2-minute tour' },
      { href: '/pricing', label: 'Pricing' },
      { href: '/start', label: 'Free notebook' },
      { href: '/machining', label: 'Machining program' },
    ],
  },
  {
    title: 'Who it’s for',
    links: [
      { href: '/for/hardware-startups', label: 'Hardware startups' },
      { href: '/for/defense-aerospace', label: 'Defense & aerospace' },
      { href: '/for/university-teams', label: 'University teams' },
    ],
  },
  {
    title: 'Resources',
    links: [
      { href: '/resources', label: 'All resources' },
      { href: '/resources/what-is-an-engineering-operating-system', label: 'What is an Engineering OS?' },
      { href: '/resources/engineering-operating-system-vs-plm', label: 'Factory OS vs. PLM/PDM' },
      { href: '/resources/design-evidence-as-byproduct', label: 'Evidence as a byproduct' },
      { href: '/faq', label: 'FAQ' },
    ],
  },
  {
    title: 'Company',
    links: [
      { href: '/security', label: 'Security & trust' },
      { href: '/contact', label: 'Contact' },
      { href: SIGN_IN_URL, label: 'Sign in' },
    ],
  },
];

const LEGAL_LINKS = [
  { href: '/privacy', label: 'Privacy Policy' },
  { href: '/accessibility', label: 'Accessibility' },
  { href: '/terms', label: 'Terms of Use' },
  { href: '/contact', label: 'Contact' },
];

/* "Request access" links straight to the app's request-access flow. */
const LegalFooter = ({ variant = 'default' }) => {
  const year = new Date().getFullYear();

  return (
    <footer
      className="w-full px-5 py-10"
      style={{
        background: 'transparent',
        borderTop: '1px solid var(--wb-line)',
        scrollSnapAlign: variant === 'cinematic' ? 'start' : undefined,
        fontFamily: sans,
      }}
      aria-labelledby="footer-heading"
    >
      <h2 id="footer-heading" className="sr-only">Footer</h2>
      <div className="max-w-[1080px] mx-auto">
        {/* Top row: brand + link columns */}
        <div className="grid grid-cols-2 md:grid-cols-[1.4fr_1fr_1fr_1fr_1fr] gap-8">
          <div className="col-span-2 md:col-span-1">
            <a
              href="/"
              className="no-underline"
              style={{ fontFamily: serif, fontWeight: 500, fontSize: 'var(--t-md)', letterSpacing: '-0.02em', color: 'var(--wb-ink)' }}
            >
              Factory OS
            </a>
            <p className="mt-2 max-w-[360px]" style={{ fontFamily: sans, fontSize: 'var(--t-md)', color: 'var(--wb-muted)', lineHeight: 1.5 }}>
              The guided engineering loop for hardware.
            </p>
            <a
              href={REQUEST_ACCESS_URL}
              className="mt-3 inline-block no-underline"
              style={{ fontFamily: mono, fontSize: 'var(--t-sm)', color: 'var(--wb-orange)', letterSpacing: '0.04em', fontWeight: 700 }}
            >
              Request access →
            </a>
          </div>

          {COLUMNS.map((col) => (
            <nav key={col.title} aria-label={col.title}>
              <div style={{ fontFamily: mono, fontSize: 'var(--t-sm)', color: 'var(--wb-faint)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 10, fontWeight: 700 }}>
                {col.title}
              </div>
              <ul className="list-none p-0 m-0 space-y-2">
                {col.links.map((l) => (
                  <li key={l.href}>
                    <a
                      href={l.href}
                      className="no-underline"
                      style={{ fontFamily: sans, fontSize: 'var(--t-md)', color: 'var(--wb-ink-2)' }}
                    >
                      {l.label}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          ))}
        </div>

        {/* Legal row */}
        <div
          className="mt-8 pt-5 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4"
          style={{ borderTop: '1px solid var(--wb-line)' }}
        >
          <nav aria-label="Legal" className="flex flex-wrap gap-x-5 gap-y-2">
            {LEGAL_LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                className="no-underline"
                style={{ fontFamily: mono, fontSize: 'var(--t-sm)', color: 'var(--wb-ink-2)', letterSpacing: '0.03em' }}
              >
                {l.label}
              </a>
            ))}
          </nav>
          <p style={{ fontFamily: mono, fontSize: 'var(--t-sm)', color: 'var(--wb-muted)', letterSpacing: '0.03em' }}>
            &copy; {year} Factory OS. All rights reserved.
          </p>
        </div>

        <p className="mt-4" style={{ fontFamily: mono, fontSize: 'var(--t-sm)', color: 'var(--wb-faint)' }}>
          Factory OS is under active development. Features and interfaces will continue evolving based on user feedback.
        </p>
      </div>
    </footer>
  );
};

export default LegalFooter;
